import { DeviceState, DisplayProcessingLogics, StatesToDisplayItem, PoolDeviceCriticalDesiredActual, DisplayWidths, PoolDeviceCriticalFieldPropertyAndValue } from "../Types";
import { IOnoffable, PoolDeviceInterface } from "../Interfaces";
import Locale from "../../utils/Locale";
import { DeviceTypes, DeviceConfig } from "../../config/documents/HardwareConfig";
import PoolDeviceOnOffable from "./base-onoffable";
import { ValueAndUnit } from "../../types";
import { getPumpInfo } from "../../catalog";


export enum criticalFieldIdentifiers {
    OnOff = "OnOff",
    SpeedPercent = "SpeedPercent",
}

export default class PoolDevicePump extends PoolDeviceOnOffable implements PoolDeviceInterface, IOnoffable {
    static CanBeAddedToflow = true;
    static DeviceType = DeviceTypes.PUMP;
    static Icon = "pump";
    OnOffIdentifier = criticalFieldIdentifiers.OnOff;
    static PickerTitle = Locale.gettext("Pump");
    
    formatDeviceStatusCustom(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {
        switch(DisplayProcessingLogic) {
        case DisplayProcessingLogics.WATTAGE:
            return { value, separator: " ", unit: "W" };
        case DisplayProcessingLogics.RPM:
            return { value, separator: " ", unit: Locale.gettext("RPM") };
        default: return super.formatDeviceStatusCustom(DisplayProcessingLogic, value);
        }
    }

    getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue: PoolDeviceCriticalFieldPropertyAndValue[]) {
        // Don't show disagree box for speed in case the pump is priming
        const identifiers = super.getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue);
        const primingUntil = this.getDeviceState(PoolsideDaemonDeviceStateNames.PrimingUntil)?.value;
        return identifiers.filter(item => (
            item !== criticalFieldIdentifiers.SpeedPercent ||
            !primingUntil
        ));
    }

    getDisplayNameTranslated(displayName:string) {
        switch (displayName) {
        case "Power consumption": return Locale.gettext("Power consumption");
        case "Power": return Locale.gettext("Power");
        case "Priming Until": return Locale.gettext("Priming Until");    
        default: return super.getDisplayNameTranslated(displayName);
        }
    }

    getDeviceState(name: string): DeviceState {
        const state = super.getDeviceState(name);
        // Controller JSON encode PrimingUntil so it is e.g: "\"2024....\""
        if ((name === PoolsideDaemonDeviceStateNames.PrimingUntil && typeof state?.value === "string") ||
            (name === PoolsideDaemonDeviceStateNames.CoolingDownUntil && typeof state?.value === "string")
        ) {
            return {
                ...state,
                value: JSON.parse(state.value)
            };
        } else {
            return state;
        }
    }
    
    getCriticalFields(): PoolDeviceCriticalDesiredActual[] {
        let order = 0;
        const fields = [];
    
        {
            const DisplayName = Locale.gettext("Power");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.ONOFF,
                DisplayProcessingLogicUnitAsDisplayName: false,
                DisplayDiffTimeout: 10,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.OnOff,
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.ActualPowerState }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.PowerState }, common)
            });
        }
        {
            const DisplayName = Locale.gettext("Speed");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.PERCENT,
                DisplayProcessingLogicUnitAsDisplayName: false,
                DisplayDiffTimeout: 10,
                DisplayDiffThreshold: 5,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.SpeedPercent,
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.ActualSpeedPercent }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.DesiredSpeedPercent }, common)
            });
        }
    
        return fields;
    }
    
    getInformationFields(): StatesToDisplayItem[] {
        let order = 0;
        const fields = [];
    
        const pumpInfo = getPumpInfo(this.deviceConfig, undefined, this.siteStates.CatalogVersion);
    
        {
            fields.push({
                Name: PoolsideDaemonDeviceStateNames.Watts,
                DisplayName: "Power consumption",
                DisplayNameShort: "Power",
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.WATTAGE,
                DisplayProcessingLogicUnitAsDisplayName: true,
            });
        }
        {
            const DisplayName = "RPM";
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.RPM,
                DisplayProcessingLogicUnitAsDisplayName: true,
            };
            fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.RPM }, common));
        }
        if (this.deviceConfig?.HardwareDevice?.Make === "Pentair" && pumpInfo?.gpm === "Y") {
            const DisplayName = "Flow";
            fields.push({
                Name: PoolsideDaemonDeviceStateNames.GPM,
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.GPM,
                DisplayProcessingLogicUnitAsDisplayName: true,
            });
        }
        if (this.deviceConfig?.HardwareDevice?.Make === "Pentair" && pumpInfo?.gpm === "Y") {
            const DisplayName = "Pressure";
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.PSI,
                DisplayProcessingLogicUnitAsDisplayName: true,
            };
            fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.PSI }, common));
        }
        if (this.deviceConfig?.HardwareDevice?.PumpSettings?.PrimeDurationMinutes) {
            const DisplayName = "Priming Until";
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.DATETIME,
                DisplayProcessingLogicUnitAsDisplayName: false,
                DisplayWidth: DisplayWidths.WIDE,
            };
            fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.PrimingUntil }, common));
        }
        
        const DisplayName = "Heater Cooldown Until";
        const common = {
            DisplayName, DisplayNameShort: DisplayName,
            DisplayOrder: ++order,
            DisplayProcessingLogic: DisplayProcessingLogics.DATETIME,
            DisplayProcessingLogicUnitAsDisplayName: false,
            DisplayWidth: DisplayWidths.WIDE,
        };
        fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.CoolingDownUntil }, common));
    
    
        return fields;
    }
}

export enum PoolsideDaemonDeviceStateNames {
    ActualPowerState = "ActualPowerState",
    ActualSpeedPercent = "ActualSpeedPercent",
    CoolingDownUntil = "CoolingDownUntil",
    DesiredGPM = "DesiredGPM",
    DesiredSpeedPercent = "DesiredSpeedPercent",
    GPM = "GPM",
    RPM = "RPM",
    PowerState = "PowerState",
    PrimingUntil = "PrimingUntil",
    PSI = "PSI",
    Watts = "Watts",
}
